import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Footprints, ArrowLeft } from "lucide-react";

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "siswa", kelas: 10 });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        const payload = { name: form.name, email: form.email, password: form.password, role: form.role };
        if (form.role === "siswa") payload.kelas = Number(form.kelas);
        await register(payload);
      }
      toast.success("Berhasil masuk!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] grain flex flex-col">
      <div className="max-w-6xl w-full mx-auto px-4 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2 text-[#5C5F5C] hover:text-[#1A1C1A] transition-colors text-sm">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6 justify-center">
            <img src="/talenta-logo.png" alt="TalentaPath" className="h-11 w-11 rounded-full object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-heading font-black text-xl text-[#1A1C1A]">TalentaPath</span>
              <span className="text-xs font-medium text-[#E25E3E]">powered by Talenta</span>
            </span>
          </div>
          <div className="clay-card p-7">
            <div className="flex rounded-full bg-[#F0ECE4] p-1 mb-6">
              {["login", "register"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`tab-${m}`}
                  className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${mode === m ? "bg-white text-[#1A1C1A] shadow-sm" : "text-[#5C5F5C]"}`}>
                  {m === "login" ? "Masuk" : "Daftar"}
                </button>
              ))}
            </div>

            <h1 className="font-heading font-black text-2xl mb-1 text-[#1A1C1A]">
              {mode === "login" ? "Selamat datang kembali" : "Buat akun baru"}
            </h1>
            <p className="text-sm text-[#5C5F5C] mb-6">
              {mode === "login" ? "Masuk untuk melihat hasil & langkahmu." : "Mulai perjalanan menemukan potensimu."}
            </p>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <>
                  <Field label="Nama Lengkap">
                    <input required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="input-name"
                      className="lg-input" placeholder="Nama kamu" />
                    <p className="text-xs text-[#5C5F5C] mt-1">Gunakan nama lengkap sesuai yang terdaftar di Bimbel Talenta agar hasil TryOut dapat diterima dengan benar.</p>
                  </Field>
                  <Field label="Saya adalah">
                    <div className="grid grid-cols-2 gap-2">
                      {[["siswa", "Siswa"], ["orangtua", "Orang Tua"]].map(([v, l]) => (
                        <button type="button" key={v} onClick={() => upd("role", v)} data-testid={`role-${v}`}
                          className={`py-2.5 rounded-lg border text-sm font-medium transition-colors ${form.role === v ? "border-[#E25E3E] bg-[#E25E3E]/10 text-[#E25E3E]" : "border-[#E5E0D8] text-[#5C5F5C]"}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </Field>
                  {form.role === "siswa" && (
                    <Field label="Kelas">
                      <select value={form.kelas} onChange={(e) => upd("kelas", e.target.value)} data-testid="input-kelas" className="lg-input">
                        <option value={9}>Kelas 9 (SMP)</option>
                        <option value={10}>Kelas 10 (SMA)</option>
                        <option value={11}>Kelas 11 (SMA)</option>
                        <option value={12}>Kelas 12 (SMA)</option>
                      </select>
                    </Field>
                  )}
                </>
              )}
              <Field label="Email">
                <input required type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="input-email"
                  className="lg-input" placeholder="email@contoh.com" />
              </Field>
              <Field label="Kata Sandi">
                <input required type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="input-password"
                  className="lg-input" placeholder="Minimal 6 karakter" />
              </Field>
              <button type="submit" disabled={loading} data-testid="submit-auth-btn"
                className="w-full py-3.5 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors disabled:opacity-60">
                {loading ? "Memproses…" : mode === "login" ? "Masuk" : "Daftar & Mulai"}
              </button>
            </form>

            {mode === "login" && (
              <div className="mt-4 text-center space-y-2">
                <Link to="/lupa-password" data-testid="forgot-password-link" className="text-sm text-[#E25E3E] font-medium hover:underline">
                  Lupa kata sandi?
                </Link>
                <p className="text-xs text-[#5C5F5C]">
                  Coba akun demo: <span className="font-semibold">siswa@demo.com</span> / <span className="font-semibold">Demo1234!</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`.lg-input{width:100%;padding:0.7rem 0.9rem;border-radius:0.6rem;border:1px solid #E5E0D8;background:#fff;outline:none;font-size:0.95rem}.lg-input:focus{border-color:#E25E3E;box-shadow:0 0 0 3px rgba(226,94,62,0.15)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1A1C1A] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
